import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@ideoz/ui';
import { CloudUpload, FileText, Loader2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { iceService } from '@ideoz/core';
import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENT_SIZE_MB,
} from '@ideoz/core/utils/attachment-types';
import { useScopeIndexing } from '../useScopeIndexing';
import { ScopeAnalyzingView } from './ScopeAnalyzingView';

export interface ScopeAttachment {
  id: string;
  fileName: string;
  fileSize?: number;
}

export interface CreateScopeDialogSubmit {
  title: string;
  description?: string;
  newFiles: File[];
}

export interface CreateScopeDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Persists the scope. Return the new scope's id on create so the dialog can
   * gate on file indexing in place; edit/void skips the gate.
   */
  onSubmit: (data: CreateScopeDialogSubmit) => Promise<string | void>;
  /** Required to poll attachment readiness for the in-dialog analyzing gate. */
  startupId?: string;
  /**
   * Queues an ICE evaluation for the newly-created scope. Prefer the parent's
   * ICE facade `evaluate` so the card shows "Scoring…" and polls for the result;
   * when omitted, the dialog falls back to a raw fire-and-forget API call.
   */
  onEvaluate?: (scopeId: string) => void;
  isLoading?: boolean;
  initialValues?: { title?: string; description?: string };
  mode?: 'create' | 'edit';
  /**
   * The scope being edited. Required in `edit` mode so the dialog can queue an
   * ICE re-evaluation after a successful save (create gets the id from onSubmit).
   */
  scopeId?: string;
  existingAttachments?: ScopeAttachment[];
  onDeleteExistingAttachment?: (id: string) => Promise<void>;
}

const ALLOWED_EXTENSIONS = ALLOWED_ATTACHMENT_EXTENSIONS;
const MAX_FILE_SIZE = MAX_ATTACHMENT_SIZE_BYTES;

const fileExtension = (name: string) => name.split('.').pop()?.toLowerCase() ?? '';

const formatSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

export const CreateScopeDialog: React.FC<CreateScopeDialogProps> = ({
  open,
  onClose,
  onSubmit,
  startupId,
  onEvaluate,
  isLoading = false,
  initialValues,
  mode = 'create',
  scopeId,
  existingAttachments = [],
  onDeleteExistingAttachment,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Tracks whether an already-saved attachment was removed during this edit —
  // deletions persist immediately (not on Save), but they still count as a
  // change so "Save Changes" can enable and re-evaluate.
  const [hasDeletedAttachment, setHasDeletedAttachment] = useState(false);
  // When set, the created scope's files are being indexed and the dialog shows
  // the analyzing gate instead of the form.
  const [preparing, setPreparing] = useState<{
    scopeId: string;
    files: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Prefers the parent's facade `evaluate` (drives the "Scoring…" badge +
  // polling); otherwise a raw fire-and-forget.
  const runEvaluate = (scopeId: string) => {
    if (onEvaluate) {
      onEvaluate(scopeId);
      return;
    }
    if (!startupId) return;
    void iceService.evaluateScope(startupId, scopeId).catch((err) => {
      console.error('Failed to trigger scope evaluation', err);
    });
  };

  // Poll the created scope's files until indexed; on completion, evaluate the
  // scope (file-aware) and close.
  const { rows, indexed, analyzing, total } = useScopeIndexing({
    startupId,
    scopeId: preparing?.scopeId ?? null,
    fileNames: preparing?.files ?? [],
    onComplete: (scopeId) => {
      runEvaluate(scopeId);
      setPreparing(null);
      onClose();
    },
  });

  useEffect(() => {
    if (open) {
      setTitle(initialValues?.title ?? '');
      setDescription(initialValues?.description ?? '');
      setPendingFiles([]);
      setFileError(null);
      setPreparing(null);
      setHasDeletedAttachment(false);
    }
  }, [open, initialValues]);

  const acceptFiles = (incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    const errors: string[] = [];
    const accepted: File[] = [];
    for (const file of list) {
      const ext = fileExtension(file.name);
      if (!(ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) {
        errors.push(t('scopeDialog.unsupportedType', { name: file.name }));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(t('scopeDialog.overSize', { name: file.name, size: MAX_ATTACHMENT_SIZE_MB }));
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length) setPendingFiles((prev) => [...prev, ...accepted]);
    setFileError(errors.length ? errors.join(', ') : null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) acceptFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) acceptFiles(e.dataTransfer.files);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExisting = async (id: string) => {
    if (!onDeleteExistingAttachment) return;
    setDeletingId(id);
    try {
      await onDeleteExistingAttachment(id);
      setHasDeletedAttachment(true);
    } finally {
      setDeletingId(null);
    }
  };

  const isCreate = mode === 'create';

  // Edit-mode "dirty" check: whether anything actually changed vs. the loaded
  // scope. Drives the "Save Changes" disabled state (create only needs a title).
  const initialTitle = (initialValues?.title ?? '').trim();
  const initialDescription = (initialValues?.description ?? '').trim();
  const isDirty =
    title.trim() !== initialTitle ||
    description.trim() !== initialDescription ||
    pendingFiles.length > 0 ||
    hasDeletedAttachment;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || isLoading) return;
    // Edit: nothing changed → no-op (button is disabled anyway; guard for Enter).
    if (!isCreate && !isDirty) return;
    const fileCount = pendingFiles.length;
    const result = await onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      newFiles: pendingFiles,
    });
    // The scope to (re-)evaluate: the new id on create, the edited scope on edit.
    const evalScopeId = isCreate
      ? typeof result === 'string' && result
        ? result
        : undefined
      : scopeId;
    if (startupId && evalScopeId) {
      // Files were added → keep the dialog open and show the analyzing gate;
      // useScopeIndexing queues the (re-)evaluation once every file is indexed.
      // Applies to both create and edit.
      if (fileCount > 0) {
        setPreparing({ scopeId: evalScopeId, files: pendingFiles.map((f) => f.name) });
        return;
      }
      // No files to index → (re-)evaluate right away.
      runEvaluate(evalScopeId);
    }
    onClose();
  };

  const submitLabel = isCreate ? t('scopeDialog.create') : t('scopeDialog.save');
  const busyLabel = isCreate ? t('scopeDialog.creating') : t('scopeDialog.saving');
  const submitDisabled = isLoading || !title.trim() || (!isCreate && !isDirty);

  if (preparing) {
    return (
      <Dialog open={open}>
        <DialogContent
          className="sm:max-w-lg"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <ScopeAnalyzingView rows={rows} indexed={indexed} analyzing={analyzing} total={total} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !isLoading && onClose()}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
        <div className="flex items-start justify-between px-6 pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {isCreate ? t('scopeDialog.addTitle') : t('scopeDialog.editTitle')}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 mt-0.5">
                {t('scopeDialog.subtitle')}
              </DialogDescription>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !isLoading && onClose()}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={t('scopeDialog.cancel')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="h-px bg-gray-100 mt-5" />

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          <div className="space-y-1.5">
            <Label htmlFor="scope-title" className="font-medium">
              {t('scopeDialog.nameLabel')} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="scope-title"
              placeholder={t('scopeDialog.namePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scope-details" className="font-medium">
              {t('scopeDialog.detailsLabel')}
            </Label>
            <Textarea
              id="scope-details"
              placeholder={t('scopeDialog.detailsPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label className="font-medium">{t('scopeDialog.attachments')}</Label>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={[
                'rounded-xl border border-dashed px-6 py-8 text-center cursor-pointer transition-colors bg-background',
                isDragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50',
              ].join(' ')}
              role="button"
              tabIndex={0}
            >
              <div className="flex flex-col items-center gap-2">
                <CloudUpload className="w-6 h-6 text-gray-400" />
                <p className="text-sm font-medium text-gray-700">{t('scopeDialog.dropzone')}</p>
                <p className="text-xs text-gray-500">
                  {t('scopeDialog.dropzoneHint', { size: MAX_ATTACHMENT_SIZE_MB })}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleFileInputChange}
                accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',')}
              />
            </div>

            {fileError && <p className="text-xs text-red-500">{fileError}</p>}

            {(existingAttachments.length > 0 || pendingFiles.length > 0) && (
              <ul className="space-y-2 pt-1">
                {existingAttachments.map((att) => (
                  <li
                    key={att.id}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{att.fileName}</p>
                      {att.fileSize ? (
                        <p className="text-xs text-gray-400">{formatSize(att.fileSize)}</p>
                      ) : null}
                    </div>
                    {onDeleteExistingAttachment && (
                      <button
                        type="button"
                        onClick={() => handleDeleteExisting(att.id)}
                        disabled={deletingId === att.id}
                        className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        aria-label={t('scopeDialog.removeAria', { name: att.fileName })}
                      >
                        {deletingId === att.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </li>
                ))}
                {pendingFiles.map((file, idx) => (
                  <li
                    key={`pending-${idx}-${file.name}`}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{file.name}</p>
                      <p className="text-xs text-gray-400">
                        {formatSize(file.size)} · {t('scopeDialog.pendingUpload')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePendingFile(idx)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      aria-label={t('scopeDialog.removeAria', { name: file.name })}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </form>

        <DialogFooter className="px-6 py-4 bg-gray-50 border-t border-gray-100 sm:justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            {t('scopeDialog.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled} className="gap-1.5">
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {isLoading ? busyLabel : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateScopeDialog;
